import { useState, useRef, useCallback, useEffect } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { loadAllowedDomains, isAllowedScannedUrl } from "../utils/scanner/domainValidators";
import { getLocation } from "../utils/scanner/locationUtils";
import { fetchScanData, saveTempScan } from "../services/scannerService";

const useScanner = ({ user, onStopScan, setUniqueKey, setUserID }) => {
    const [message, setMessage] = useState("");
    const [instructions, setInstructions] = useState("");
    const [showRetry, setShowRetry] = useState(false);
    const [redundantScanInfo, setRedundantScanInfo] = useState(null);

    const qrReaderRef = useRef(null);
    const isProcessingRef = useRef(false);
    const tempListenerCleanupRef = useRef(null);

    const allowedList = loadAllowedDomains();

    /* ---------- Scanner Cleanup ---------- */

    const stopAndClearScanner = async () => {
        if (!qrReaderRef.current) return;
        try {
            await qrReaderRef.current.stop?.();
            await qrReaderRef.current.clear?.();
        } catch (err) {
            console.warn("Error stopping scanner:", err);
        } finally {
            qrReaderRef.current = null;
        }
    };

    /* ---------- Redundant Scan Handler ---------- */

    const handleRedundantScan = async (scanID, userPath) => {
        await stopAndClearScanner();

        let scanData = null;
        try {
            scanData = await fetchScanData(userPath, scanID);
        } catch (err) {
            console.error("Error fetching redundant scan data:", err);
        }

        const needsAddress = !scanData || scanData.addedToMautic === null || scanData.addedToMautic === undefined;
        const needsPhoto = !scanData || !scanData.urlToReadImage;

        setMessage("QR Code Already Scanned");

        if (needsAddress || needsPhoto) {
            setInstructions(
                "This QR code has already been scanned, but it's incomplete.\n\n" +
                "Complete the missing steps below:"
            );
        } else {
            setInstructions(
                "This QR code cannot be used at this location or has already been scanned.\n\n" +
                "Please scan a different QR code."
            );
        }

        setShowRetry(true);
        setRedundantScanInfo({ scanKey: scanID, userPath, needsAddress, needsPhoto });
    };

    /* ---------- Save Temp Scan & Wait ---------- */

    const saveTempScanAndWait = async (decodedText, latitude, longitude, accuracy) => {
        const userPath = user?.email?.replaceAll(".", "-") || "unknown-user";
        const scanID = decodedText.replaceAll(/[^a-zA-Z0-9_-]/g, "-");

        setMessage("Verifying...");
        setInstructions("");
        setShowRetry(false);

        // Clean up any lingering listener from a previous scan
        if (tempListenerCleanupRef.current) {
            tempListenerCleanupRef.current();
            tempListenerCleanupRef.current = null;
        }

        const { promise, cleanup } = saveTempScan(userPath, scanID, decodedText, latitude, longitude, accuracy);
        tempListenerCleanupRef.current = cleanup;

        const result = await promise;
        tempListenerCleanupRef.current = null;

        const { redundant, redundantLoc } = result;

        if (redundant === true || redundantLoc === true) {
            await handleRedundantScan(scanID, userPath);
            return;
        }

        if (redundant === false && redundantLoc === false) {
            setUniqueKey?.(scanID);
            setUserID?.(userPath);
            onStopScan?.();
        }
    };

    /* ---------- QR Handler ---------- */

    const handleDecodedText = async (decodedText) => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            if (!isAllowedScannedUrl(decodedText, allowedList)) {
                await stopAndClearScanner();
                setMessage("Invalid QR Code");
                setInstructions("Please scan a valid QR code.");
                setShowRetry(true);
                return;
            }

            if (navigator.vibrate) {
                try { navigator.vibrate(200); } catch (e) { console.error("Vibration failed", e); }
            }

            const position = await getLocation();
            await stopAndClearScanner();
            await saveTempScanAndWait(
                decodedText,
                position.coords.latitude,
                position.coords.longitude,
                position.coords.accuracy
            );
        } catch (err) {
            await stopAndClearScanner();

            if (err?.code === 2) {
                setMessage("Location Permission Blocked");
                setInstructions(
                    "1. Tap the 🔒 Lock icon in your browser address bar.\n" +
                    "2. Open Permissions or Location settings.\n" +
                    "3. Allow location access.\n" +
                    "4. Then retry."
                );
                setShowRetry(true);
                return;
            }

            console.error("Scanner error:", err);
            setMessage("Camera / Location Permission Blocked");
            setInstructions(
                "1. Tap the 🔒 Lock icon in your browser address bar.\n" +
                "2. Open Permissions or Location settings.\n" +
                "3. Allow required permissions.\n" +
                "4. Then retry."
            );
            setShowRetry(true);
        } finally {
            isProcessingRef.current = false;
        }
    };

    /* ---------- Start Scanner ---------- */

    const startScanner = useCallback(async () => {
        try {
            setMessage("");
            setInstructions("");
            setShowRetry(false);
            setRedundantScanInfo(null);
            isProcessingRef.current = false;

            await stopAndClearScanner();

            const qrReader = new Html5Qrcode("reader");
            qrReaderRef.current = qrReader;

            await qrReader.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 400, height: 400 } },
                handleDecodedText
            );
        } catch (err) {
            console.error("Scanner start error:", err);
            setMessage("Camera / Location Permission Blocked");
            setInstructions(
                "1. Tap the 🔒 Lock icon in your browser address bar.\n" +
                "2. Open Permissions or Location settings.\n" +
                "3. Allow required permissions.\n" +
                "4. Then retry."
            );
            setShowRetry(true);
        }
    }, []);

    /* ---------- Lifecycle ---------- */

    useEffect(() => {
        startScanner();
        return () => {
            stopAndClearScanner();
            // Clean up any lingering temp scan listener on unmount
            if (tempListenerCleanupRef.current) {
                tempListenerCleanupRef.current();
                tempListenerCleanupRef.current = null;
            }
        };
    }, [startScanner]);

    return { message, instructions, showRetry, startScanner, redundantScanInfo };
};

export default useScanner;
